import React from "react";

export type TenantDeletionSpinnerSize = "sm" | "md";
export type TenantDeletionSpinnerVariant = "primary" | "onAccent";

export interface TenantDeletionSpinnerProps {
  /** "sm" = inline (e.g. button), "md" = page-level loading state. */
  size?: TenantDeletionSpinnerSize;
  /** "primary" = brand color border (page loaders); "onAccent" = white border (on red/dark buttons). */
  variant?: TenantDeletionSpinnerVariant;
  /** Optional test id; passed through verbatim. */
  "data-testid"?: string;
  /** Extra classes appended to the spinner element. */
  className?: string;
}

/**
 * Single reusable spinner for the tenant-deletion feature.
 * Normalises the previously inconsistent inline spinner styles across
 * AdminTenants, AdminTenantDeletions, AdminTenantDeletionDetails, and
 * DeleteTenantModal (LOW UX polish item).
 *
 * Decorative — caller is responsible for the surrounding aria-live region
 * (e.g. status announcement) when relevant.
 */
export const TenantDeletionSpinner: React.FC<TenantDeletionSpinnerProps> = ({
  size = "md",
  variant = "primary",
  "data-testid": dataTestId,
  className,
}) => {
  const sizeClass = size === "sm" ? "h-4 w-4 border-2" : "h-8 w-8 border-2";
  const variantClass =
    variant === "onAccent"
      ? "border-white border-t-transparent"
      : "border-primary-600 border-t-transparent";

  return (
    <span
      data-testid={dataTestId}
      aria-hidden="true"
      className={[
        "inline-block rounded-full animate-spin",
        sizeClass,
        variantClass,
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    />
  );
};

export default TenantDeletionSpinner;
